import type {
  ChartConfig,
  ChatAttachment,
  FlowMetadata,
  Message,
  MessageType,
} from '../types/chat.types'
import type { IHttpClient } from './httpClient'
import { generateId } from './idGenerator'
import { maxMessageDebug } from '../../utils/max-debug'
import { normalizeFlowMetadata } from '../../utils/flow'

interface BackendMessage {
  messageId?: string
  id?: string
  role?: string
  type?: string
  content?: string
  body?: string
  timestamp?: string
  createdAt?: string
  conversationId?: string
  attachments?: ChatAttachment[] | string[]
  fav?: boolean
  responseType?: string
  is_flow?: boolean
  flow_metadata?: Record<string, unknown>
  graphs?: ChartConfig[]
  tokenUsage?: {
    inputTokens: number
    outputTokens: number
    totalTokens: number
  }
}

type BackendMessagesResponse = BackendMessage[] | { messages?: BackendMessage[] }

export interface StreamStartEvent {
  requestId: string
  clientRequestId?: string
  tenantId?: string
  tempConversationId?: string
  messageId?: string
  conversationId: string
  role?: string
  timestamp?: string
  responseType?: string
  isFlow?: boolean
  flowMetadata?: FlowMetadata
  graphs?: ChartConfig[]
}

export interface StreamChunkEvent {
  requestId: string
  clientRequestId?: string
  tenantId?: string
  tempConversationId?: string
  conversationId?: string
  messageId?: string
  role?: string
  type?: string
  content: string
  index: number
  contentBlockIndex?: number
  timestamp?: string
  responseType?: string
  isFlow?: boolean
  flowMetadata?: FlowMetadata
  graphs?: ChartConfig[]
}

export interface StreamEndEvent {
  requestId: string
  clientRequestId?: string
  tenantId?: string
  tempConversationId?: string
  conversationId?: string
  messageId?: string
  role?: string
  type?: string
  stopReason?: string
  timestamp?: string
  responseType?: string
  isFlow?: boolean
  flowMetadata?: FlowMetadata
  graphs?: ChartConfig[]
}

export interface FinalResponseEvent {
  requestId: string
  clientRequestId?: string
  tenantId?: string
  conversationId: string
  response: string
  messageId?: string
  role?: string
  tempConversationId?: string
  timestamp?: string
  responseType?: string
  isFlow?: boolean
  flowMetadata?: FlowMetadata
  rawPayload?: Record<string, unknown>
  graphs?: ChartConfig[]
  tokenUsage?: BackendMessage['tokenUsage']
}

export interface StreamUpdateResult {
  requestId: string
  messageId: string
  conversationId: string
  content: string
  createdAt?: Date
  tempConversationId?: string
  responseType?: string
  isFlow?: boolean
  flowMetadata?: FlowMetadata
  rawPayload?: Record<string, unknown>
  graphs?: ChartConfig[]
  tokenUsage?: BackendMessage['tokenUsage']
}

interface StreamSession {
  requestId: string
  conversationId: string
  messageId: string
  role?: string
  createdAt?: Date
  chunks: Map<number, string>
  completed: boolean
  responseType?: string
  isFlow?: boolean
  flowMetadata?: FlowMetadata
  rawPayload?: Record<string, unknown>
  graphs?: ChartConfig[]
}

export interface IMessageService {
  startStream(event: StreamStartEvent): StreamUpdateResult
  appendStreamChunk(event: StreamChunkEvent): StreamUpdateResult | null
  endStream(event: StreamEndEvent): void
  finalizeStreamResponse(event: FinalResponseEvent): StreamUpdateResult
  /** Discard all in-flight stream sessions. */
  resetStreams(): void
  /** Discard one in-flight stream session. */
  discardStream(requestId: string): void
  getMessages(tenantId: string, conversationId: string): Promise<Message[]>
}

function normalizeMessageType(raw: BackendMessage): MessageType {
  const type = (raw.type || '').toLowerCase()
  const role = (raw.role || '').toLowerCase()
  if (type === 'user' || role === 'user') return 'user'
  if (type === 'system' || role === 'assistant') return 'ai'
  if (type === 'ai') return 'ai'
  return role === 'system' ? 'system' : 'user'
}

function mapMessage(raw: BackendMessage, fallbackConversationId: string): Message | null {
  const messageId = raw.messageId || raw.id
  if (!messageId) return null

  const rawAttachments = raw.attachments as unknown
  const attachments =
    Array.isArray(rawAttachments) &&
    (rawAttachments as unknown[]).every((item) => item && typeof item === 'object')
      ? (rawAttachments as ChatAttachment[])
      : undefined

  return {
    id: messageId,
    messageId,
    conversationId: raw.conversationId || fallbackConversationId,
    type: normalizeMessageType(raw),
    role: raw.role,
    content: raw.content || raw.body || '',
    createdAt: raw.timestamp
      ? new Date(raw.timestamp)
      : raw.createdAt
      ? new Date(raw.createdAt)
      : new Date(),
    attachments,
    fav: raw.fav,
    responseType: raw.responseType || raw.type,
    isFlow: raw.is_flow,
    flowMetadata: normalizeFlowMetadata(raw.flow_metadata as FlowMetadata | undefined) || undefined,
    graphs: raw.graphs,
    rawPayload: raw,
  }
}

export class MessageService implements IMessageService {
  private readonly streamSessions = new Map<string, StreamSession>()

  constructor(private httpClient?: IHttpClient) {}

  startStream(event: StreamStartEvent): StreamUpdateResult {
    const messageId = event.messageId || generateId()
    const createdAt = event.timestamp ? new Date(event.timestamp) : new Date()

    maxMessageDebug.event('MessageService startStream', {
      requestId: event.requestId,
      messageId,
      conversationId: event.conversationId,
      responseType: event.responseType,
      isFlow: Boolean(event.isFlow),
    })

    const session: StreamSession = {
      requestId: event.requestId,
      conversationId: event.conversationId,
      messageId,
      role: event.role,
      createdAt,
      chunks: new Map(),
      completed: false,
      responseType: event.responseType,
      isFlow: event.isFlow,
      flowMetadata: normalizeFlowMetadata(event.flowMetadata) || undefined,
      graphs: event.graphs,
    }

    this.streamSessions.set(event.requestId, session)

    return {
      requestId: event.requestId,
      messageId,
      conversationId: event.conversationId,
      content: '',
      createdAt,
      responseType: event.responseType,
      isFlow: event.isFlow,
      flowMetadata: normalizeFlowMetadata(event.flowMetadata) || undefined,
      graphs: event.graphs,
    }
  }

  appendStreamChunk(event: StreamChunkEvent): StreamUpdateResult | null {
    try {
      const session = this.ensureSessionForChunk(event)
      session.chunks.set(event.index, event.content ?? '')

      return {
        requestId: session.requestId,
        messageId: session.messageId,
        conversationId: session.conversationId,
        content: this.buildContentFromSession(session),
        createdAt: session.createdAt,
        responseType: session.responseType,
        isFlow: session.isFlow,
        flowMetadata: session.flowMetadata,
        rawPayload: session.rawPayload,
        graphs: session.graphs,
      }
    } catch (error) {
      maxMessageDebug.error('MessageService appendStreamChunk failed', {
        requestId: event.requestId,
        conversationId: event.conversationId,
        index: event.index,
        errorMessage: error instanceof Error ? error.message : String(error),
      })
      return null
    }
  }

  endStream(event: StreamEndEvent): void {
    const session = this.streamSessions.get(event.requestId)
    if (session) {
      maxMessageDebug.event('MessageService endStream', {
        requestId: event.requestId,
        messageId: session.messageId,
        conversationId: session.conversationId,
        totalChunks: session.chunks.size,
      })
      session.completed = true
      session.responseType = event.responseType ?? session.responseType
      session.isFlow = event.isFlow ?? session.isFlow
      session.flowMetadata = normalizeFlowMetadata(event.flowMetadata) ?? session.flowMetadata
      session.graphs = event.graphs ?? session.graphs
      return
    }

    maxMessageDebug.event('MessageService endStream without existing session', {
      requestId: event.requestId,
      conversationId: event.conversationId,
    })
  }

  finalizeStreamResponse(event: FinalResponseEvent): StreamUpdateResult {
    const session = this.streamSessions.get(event.requestId) || this.createSessionFromFinal(event)

    maxMessageDebug.event('MessageService finalizeStreamResponse', {
      requestId: event.requestId,
      messageId: event.messageId || session.messageId,
      conversationId: event.conversationId,
      tempConversationId: event.tempConversationId,
      hadExistingSession: this.streamSessions.has(event.requestId),
      responseLength: event.response.length,
    })

    session.conversationId = event.conversationId
    session.messageId = event.messageId || session.messageId
    session.completed = true
    session.responseType = event.responseType ?? session.responseType
    session.isFlow = event.isFlow ?? session.isFlow
    session.flowMetadata = normalizeFlowMetadata(event.flowMetadata) ?? session.flowMetadata
    session.rawPayload = event.rawPayload ?? session.rawPayload
    session.graphs = event.graphs ?? session.graphs

    const content = event.response || this.buildContentFromSession(session)

    if (event.response) {
      session.chunks = new Map([[0, event.response]])
    }

    this.streamSessions.set(event.requestId, session)

    return {
      requestId: session.requestId,
      messageId: session.messageId,
      conversationId: session.conversationId,
      content,
      createdAt: session.createdAt,
      tempConversationId: event.tempConversationId,
      responseType: session.responseType,
      isFlow: session.isFlow,
      flowMetadata: session.flowMetadata,
      rawPayload: session.rawPayload,
      graphs: session.graphs,
      tokenUsage: event.tokenUsage,
    }
  }

  resetStreams(): void {
    this.streamSessions.clear()
  }

  discardStream(requestId: string): void {
    this.streamSessions.delete(requestId)
  }

  async getMessages(tenantId: string, conversationId: string): Promise<Message[]> {
    if (!this.httpClient) {
      throw new Error('MessageService httpClient is not configured.')
    }

    const response = await this.httpClient.get<BackendMessagesResponse>('/messages', {
      params: {
        tenantId,
        conversationId,
      },
    })

    const source = Array.isArray(response) ? response : response.messages || []

    return source
      .map((entry) => mapMessage(entry, conversationId))
      .filter((entry): entry is Message => entry !== null)
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
  }

  private ensureSessionForChunk(event: StreamChunkEvent): StreamSession {
    const existing = this.streamSessions.get(event.requestId)
    if (existing) {
      if (event.messageId) {
        existing.messageId = event.messageId
      }
      if (!existing.conversationId && event.conversationId) {
        existing.conversationId = event.conversationId
      }
      if (event.responseType) {
        existing.responseType = event.responseType
      }
      if (event.isFlow !== undefined) {
        existing.isFlow = event.isFlow
      }
      if (event.flowMetadata) {
        existing.flowMetadata = normalizeFlowMetadata(event.flowMetadata) || undefined
      }
      if (event.graphs) {
        existing.graphs = event.graphs
      }
      return existing
    }

    if (!event.conversationId) {
      throw new Error('Stream chunk missing conversationId')
    }

    maxMessageDebug.event('MessageService reconstructing stream session from chunk', {
      requestId: event.requestId,
      conversationId: event.conversationId,
      messageId: event.messageId,
      index: event.index,
    })

    const messageId = event.messageId || generateId()
    const createdAt = event.timestamp ? new Date(event.timestamp) : new Date()

    const session: StreamSession = {
      requestId: event.requestId,
      conversationId: event.conversationId,
      messageId,
      role: event.role,
      createdAt,
      chunks: new Map(),
      completed: false,
      responseType: event.responseType,
      isFlow: event.isFlow,
      flowMetadata: normalizeFlowMetadata(event.flowMetadata) || undefined,
      graphs: event.graphs,
    }

    this.streamSessions.set(event.requestId, session)
    return session
  }

  private createSessionFromFinal(event: FinalResponseEvent): StreamSession {
    const createdAt = event.timestamp ? new Date(event.timestamp) : new Date()
    const messageId = event.messageId || generateId()

    maxMessageDebug.event('MessageService reconstructing stream session from final response', {
      requestId: event.requestId,
      conversationId: event.conversationId,
      messageId,
      tempConversationId: event.tempConversationId,
    })

    const session: StreamSession = {
      requestId: event.requestId,
      conversationId: event.conversationId,
      messageId,
      role: event.role,
      createdAt,
      chunks: new Map(),
      completed: false,
      responseType: event.responseType,
      isFlow: event.isFlow,
      flowMetadata: normalizeFlowMetadata(event.flowMetadata) || undefined,
      rawPayload: event.rawPayload,
      graphs: event.graphs,
    }

    this.streamSessions.set(event.requestId, session)
    return session
  }

  private buildContentFromSession(session: StreamSession): string {
    return Array.from(session.chunks.entries())
      .sort(([a], [b]) => a - b)
      .map(([, value]) => value)
      .join('')
  }
}
